import { useId } from "react";

/** The eSignIt mark: a signature stroke under a pen nib, on the indigo→cyan gradient (same as the favicon). */
export function Logo({ className }: { className?: string }) {
  // Each instance needs its own gradient id: a copy inside a hidden sidebar would otherwise blank the others.
  const id = `esignit-mark-${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2f4bd8" />
          <stop offset="1" stopColor="#06b6d4" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="16" fill={`url(#${id})`} />
      <path d="M38.5 13.5l12 12-17 17-13.5 1.5 1.5-13.5z" fill="#fff" />
      <circle cx="33" cy="31" r="3.2" fill={`url(#${id})`} />
      <path d="M13 50c5-4 9-5 12-2.5s6 2.5 10-.5 7-3 11 0" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" />
    </svg>
  );
}

/** "eSignIt" in headers. */
export function Lockup({ height = 18 }: { height?: number }) {
  return <Wordmark height={height} />;
}

/** The mark and the word; height drives the size. */
export function Wordmark({ height = 20, className = "" }: { height?: number; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span style={{ width: height * 1.25, height: height * 1.25 }} className="inline-flex">
        <Logo className="size-full" />
      </span>
      <span className="font-display font-bold text-ink tracking-tight leading-none" style={{ fontSize: height * 1.05 }}>
        e<span className="text-accent-text">Sign</span>It
      </span>
    </span>
  );
}

/** A company's own logo (PNG data) if it has one, else its name. */
export function CompanyMark({ name, logo, height = 28 }: { name: string; logo?: string; height?: number }) {
  if (logo) return <img src={logo.startsWith("data:") ? logo : `data:image/png;base64,${logo}`} alt={name} style={{ maxHeight: height, maxWidth: height * 6 }} className="object-contain" />;
  return <span className="font-display font-bold text-ink truncate" style={{ fontSize: height * 0.6 }}>{name}</span>;
}

export function MicrosoftMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 21 21" className={className} aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="var(--ms-red)" />
      <rect x="11" y="1" width="9" height="9" fill="var(--ms-green)" />
      <rect x="1" y="11" width="9" height="9" fill="var(--ms-blue)" />
      <rect x="11" y="11" width="9" height="9" fill="var(--ms-yellow)" />
    </svg>
  );
}
